/**
 * useCrewConfig.js
 *
 * Hook for managing vessel-specific crew configuration settings.
 * WBC3 Edition: 5 simple settings
 *
 * Settings:
 * 1. Enabled Positions - Filter irrelevant roles (e.g., hide "Tug Master" on a RIB)
 * 2. Certificate Warning Days - Default 60, range 14-365
 * 3. Certificate Critical Days - Default 14, range 7-60
 * 4. Default HOR Regime - Seagoing (10h/day, 77h/week) vs Inland (12h/day)
 * 5. Show Archived Crew - Toggle visibility
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';
import { CREW_POSITIONS } from '../../../config/crew/crewDefinitions';

// RBAC helper (same pattern as useChecklistConfig)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Default crew configuration
 * All positions enabled by default, sensible defaults for other settings
 */
const getDefaultConfig = () => ({
  version: 1,
  revision: 1,

  // Setting 1: Enabled positions (all enabled by default)
  enabledPositions: CREW_POSITIONS.reduce((acc, pos) => {
    acc[pos] = true;
    return acc;
  }, {}),

  // Setting 2: Certificate warning days (when to show amber warning)
  certificateWarningDays: 60,

  // Setting 3: Certificate critical days (when to show red critical)
  certificateCriticalDays: 14,

  // Setting 4: Hours of Rest regime
  // 'seagoing' = MLC standard (10h/day, 77h/week)
  // 'inland' = Inland Waters (12h/day, can be more flexible)
  horRegime: 'seagoing',

  // Setting 5: Show archived (signed-off) crew in list
  showArchivedCrew: false,

  // Metadata
  lastModified: new Date().toISOString(),
  modifiedBy: { name: 'System', userId: 'system' }
});

/**
 * HOR regime definitions (MLC compliant)
 */
export const HOR_REGIMES = {
  seagoing: {
    id: 'seagoing',
    label: 'Seagoing (MLC Standard)',
    description: 'Minimum 10 hours rest/day, 77 hours rest/week',
    minDailyRest: 10,
    minWeeklyRest: 77,
    maxDailyWork: 14,
    maxWeeklyWork: 72,
    regulation: 'MLC 2006, Regulation 2.3'
  },
  inland: {
    id: 'inland',
    label: 'Inland Waters',
    description: 'Minimum 12 hours rest/day (more flexible scheduling)',
    minDailyRest: 12,
    minWeeklyRest: 77, // Still applies
    maxDailyWork: 12,
    maxWeeklyWork: 72,
    regulation: 'Inland Waters Regulations'
  }
};

/**
 * Hook for managing crew configuration
 */
export const useCrewConfig = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Vessel-scoped storage
  const storageKey = 'crew_config';
  const { value: rawConfig, setValue: setConfig } = useStorageState(
    storageKey,
    getDefaultConfig()
  );

  // Ensure config has all required fields (handle upgrades)
  const config = useMemo(() => {
    const defaults = getDefaultConfig();
    return {
      ...defaults,
      ...rawConfig,
      // Ensure enabledPositions includes all positions (new ones added over time)
      enabledPositions: {
        ...defaults.enabledPositions,
        ...(rawConfig?.enabledPositions || {})
      }
    };
  }, [rawConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get list of enabled positions for dropdowns
   */
  const enabledPositions = useMemo(() => {
    return CREW_POSITIONS.filter(pos => config.enabledPositions[pos] !== false);
  }, [config.enabledPositions]);

  /**
   * Get all positions with their enabled state
   */
  const allPositionsWithState = useMemo(() => {
    return CREW_POSITIONS.map(pos => ({
      id: pos,
      label: pos,
      enabled: config.enabledPositions[pos] !== false
    }));
  }, [config.enabledPositions]);

  /**
   * Get current HOR regime with full details
   */
  const currentHorRegime = useMemo(() => {
    return HOR_REGIMES[config.horRegime] || HOR_REGIMES.seagoing;
  }, [config.horRegime]);

  /**
   * Get certificate thresholds
   */
  const certificateThresholds = useMemo(() => ({
    warningDays: config.certificateWarningDays,
    criticalDays: config.certificateCriticalDays
  }), [config.certificateWarningDays, config.certificateCriticalDays]);

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Update a single setting
   */
  const updateSetting = useCallback(async (settingKey, value) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useCrewConfig] Permission denied');
      return false;
    }

    const oldValue = config[settingKey];

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      [settingKey]: value,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'crew_config',
    //     {
    //       vesselId: currentVessel?.id,
    //       setting: settingKey,
    //       newValue: value,
    //       oldValue
    //     },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useCrewConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Toggle a position's enabled state
   */
  const togglePosition = useCallback(async (positionName, enabled) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useCrewConfig] Permission denied');
      return false;
    }

    const updatedPositions = {
      ...config.enabledPositions,
      [positionName]: enabled
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      enabledPositions: updatedPositions,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'crew_config_position',
    //     {
    //       vesselId: currentVessel?.id,
    //       position: positionName,
    //       enabled
    //     },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useCrewConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Update certificate warning threshold
   */
  const setCertificateWarningDays = useCallback(async (days) => {
    const clamped = Math.min(365, Math.max(14, days));
    return updateSetting('certificateWarningDays', clamped);
  }, [updateSetting]);

  /**
   * Update certificate critical threshold
   */
  const setCertificateCriticalDays = useCallback(async (days) => {
    const clamped = Math.min(60, Math.max(7, days));
    return updateSetting('certificateCriticalDays', clamped);
  }, [updateSetting]);

  /**
   * Update HOR regime
   */
  const setHorRegime = useCallback(async (regime) => {
    if (!HOR_REGIMES[regime]) {
      console.warn('[useCrewConfig] Invalid HOR regime:', regime);
      return false;
    }
    return updateSetting('horRegime', regime);
  }, [updateSetting]);

  /**
   * Toggle show archived crew
   */
  const setShowArchivedCrew = useCallback(async (show) => {
    return updateSetting('showArchivedCrew', !!show);
  }, [updateSetting]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useCrewConfig] Permission denied');
      return false;
    }

    const defaults = getDefaultConfig();
    defaults.revision = (config.revision || 0) + 1;
    defaults.lastModified = new Date().toISOString();
    defaults.modifiedBy = { name: currentUser?.name, userId: currentUser?.id };

    await setConfig(defaults);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'crew_config',
    //     { vesselId: currentVessel?.id, action: 'reset_to_defaults' },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useCrewConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Raw config
    config,

    // Computed values
    enabledPositions,
    allPositionsWithState,
    currentHorRegime,
    certificateThresholds,
    showArchivedCrew: config.showArchivedCrew,

    // Individual mutations
    togglePosition,
    setCertificateWarningDays,
    setCertificateCriticalDays,
    setHorRegime,
    setShowArchivedCrew,
    resetToDefaults,

    // Generic update
    updateSetting,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision,

    // Constants for UI
    HOR_REGIMES,
    ALL_POSITIONS: CREW_POSITIONS
  };
};

export default useCrewConfig;
